import { CustomerInfo, Offering, Purchases as PurchasesWeb, ReservedCustomerAttribute } from '@revenuecat/purchases-js';
import type {
  IPurchaseStrategy,
  CrossPlatformCustomerInfo,
  CrossPlatformOffering,
  CrossPlatformPackage,
  PurchaseResult,
  OfferingsResult,
} from './types';

export class WebPurchaseStrategy implements IPurchaseStrategy {
  private initialized = false;

  async configure(apiKey: string, userId?: string, email?: string): Promise<void> {
    try {
      await PurchasesWeb.configure({ apiKey, appUserId: userId || '' });
      if (email) {
        await PurchasesWeb.getSharedInstance().setAttributes({ [ReservedCustomerAttribute.Email]: email });
      }
      this.initialized = true;
    } catch (error) {
      console.error('Error initializing RevenueCat on web:', error);
      throw error;
    }
  }

  async getCustomerInfo(): Promise<CrossPlatformCustomerInfo> {
    this.ensureInitialized();

    try {
      const SDK = PurchasesWeb.getSharedInstance();
      const result = await SDK.getCustomerInfo();

      return this.normalizeCustomerInfo(result);
    } catch (error) {
      console.error('Error fetching customer info on web:', error);
      throw error;
    }
  }

  async getOfferings(): Promise<OfferingsResult> {
    this.ensureInitialized();

    try {
      const SDK = PurchasesWeb.getSharedInstance();
      const offerings = await SDK.getOfferings();

      const currentOffering = offerings.current;
      const fullOffer = offerings.all['full_offer'];
      const launchOffer = offerings.all['launch_offer'];
      const offeringToReturn = currentOffering ?? fullOffer ?? launchOffer;
      return {
        current: offeringToReturn ? this.normalizeOffering(offeringToReturn) : undefined,
      };
    } catch (error) {
      console.error('Error fetching offerings on web:', error);
      throw error;
    }
  }

  async purchasePackage(packageToPurchase: CrossPlatformPackage): Promise<PurchaseResult> {
    this.ensureInitialized();

    try {
      const SDK = PurchasesWeb.getSharedInstance();
      const result = await SDK.purchase({ rcPackage: packageToPurchase.originalPackage });

      return {
        customerInfo: this.normalizeCustomerInfo(result.customerInfo),
      };
    } catch (error) {
      console.error('Error making purchase on web:', error);
      throw error;
    }
  }

  async restorePurchases(): Promise<CrossPlatformCustomerInfo> {
    this.ensureInitialized();

    try {
      // For web, restoring purchases is typically just refreshing customer info
      // as web subscriptions are managed through the web store
      const SDK = PurchasesWeb.getSharedInstance();
      const result = await SDK.getCustomerInfo();

      return this.normalizeCustomerInfo(result);
    } catch (error) {
      console.error('Error restoring purchases on web:', error);
      throw error;
    }
  }

  async setCustomerAttributes(attributes: Record<string, string>): Promise<void> {
    this.ensureInitialized();

    try {
      const SDK = PurchasesWeb.getSharedInstance();
      await SDK.setAttributes(attributes);
    } catch (error) {
      console.error('Error setting customer attributes on native:', error);
      throw error;
    }
  }

  private ensureInitialized(): void {
    if (!this.initialized) {
      throw new Error('WebPurchaseStrategy not initialized. Call configure() first.');
    }
  }

  private normalizeCustomerInfo(info: CustomerInfo): CrossPlatformCustomerInfo {
    const pro = info.entitlements?.active?.Pro;
    return {
      managementURL: info.managementURL,
      entitlement: pro
        ? {
            identifier: pro.identifier || '',
            willRenew: pro.willRenew || false,
            originalPurchaseDate: pro.originalPurchaseDate?.toISOString() || '',
            expirationDate: pro.expirationDate?.toISOString() || null,
          }
        : undefined,
    };
  }

  private normalizeOffering(offering: Offering): CrossPlatformOffering {
    return {
      annual: {
        displayTitle: 'Annual',
        hasTrial: true,
        cardBadgeText: `Save ${this.calculateSavePercentage(
          offering.annual?.webBillingProduct.defaultSubscriptionOption?.base.price?.amountMicros,
          offering.monthly?.webBillingProduct.defaultSubscriptionOption?.base.price?.amountMicros
            ? offering.monthly?.webBillingProduct.defaultSubscriptionOption?.base.price?.amountMicros * 12
            : undefined
        )}%`,
        drawerBadgeText: '7 Days Free',
        billingPeriod: 'annually',
        billingPeriodNoun: 'year',
        comparisonPriceString: offering.annual?.webBillingProduct.defaultSubscriptionOption?.base.pricePerWeek?.formattedPrice || '',
        comparisonPriceBillingNoun: 'week',
        formattedPrice: offering.annual?.webBillingProduct.defaultSubscriptionOption?.base.price?.formattedPrice || '',
        pricePerWeekString: offering.annual?.webBillingProduct.defaultSubscriptionOption?.base.pricePerWeek?.formattedPrice || '',
        pricePerMonthString: offering.annual?.webBillingProduct.defaultSubscriptionOption?.base.pricePerMonth?.formattedPrice || '',
        pricePerYearString: offering.annual?.webBillingProduct.defaultSubscriptionOption?.base.pricePerYear?.formattedPrice || '',
        originalPackage: offering.annual,
      },
      monthly: {
        displayTitle: 'Monthly',
        hasTrial: true,
        drawerBadgeText: '7 Days Free',
        billingPeriod: 'monthly',
        billingPeriodNoun: 'month',
        comparisonPriceString: offering.monthly?.webBillingProduct.defaultSubscriptionOption?.base.pricePerYear?.formattedPrice || '',
        comparisonPriceBillingNoun: 'year',
        formattedPrice: offering.monthly?.webBillingProduct.defaultSubscriptionOption?.base.price?.formattedPrice || '',
        pricePerWeekString: offering.monthly?.webBillingProduct.defaultSubscriptionOption?.base.pricePerWeek?.formattedPrice || '',
        pricePerMonthString: offering.monthly?.webBillingProduct.defaultSubscriptionOption?.base.pricePerMonth?.formattedPrice || '',
        pricePerYearString: offering.monthly?.webBillingProduct.defaultSubscriptionOption?.base.pricePerYear?.formattedPrice || '',
        originalPackage: offering.monthly,
      },
      weekly: {
        displayTitle: 'Weekly',
        hasTrial: false,
        billingPeriod: 'weekly',
        billingPeriodNoun: 'week',
        comparisonPriceString: offering.weekly?.webBillingProduct.defaultSubscriptionOption?.base.pricePerYear?.formattedPrice || '',
        comparisonPriceBillingNoun: 'year',
        formattedPrice: offering.weekly?.webBillingProduct.defaultSubscriptionOption?.base.price?.formattedPrice || '',
        pricePerWeekString: offering.weekly?.webBillingProduct.defaultSubscriptionOption?.base.pricePerWeek?.formattedPrice || '',
        pricePerMonthString: offering.weekly?.webBillingProduct.defaultSubscriptionOption?.base.pricePerMonth?.formattedPrice || '',
        pricePerYearString: offering.weekly?.webBillingProduct.defaultSubscriptionOption?.base.pricePerYear?.formattedPrice || '',
        originalPackage: offering.weekly,
      },
    };
  }

  private calculateSavePercentage(discountedPrice: number | undefined, originalPrice: number | undefined): number | undefined {
    if (!discountedPrice || !originalPrice) {
      return undefined;
    }
    return Math.round(((originalPrice - discountedPrice) / originalPrice) * 100);
  }
}
